import React, { useState, useEffect } from "react";
import { removeBackground } from "@imgly/background-removal"; // BG removal library
import axios from "axios";
import {
  MDBContainer,
  MDBRow,
  MDBCol,
  MDBBtn,
  MDBInput,
  MDBTextArea,
  MDBCheckbox,
} from "mdb-react-ui-kit";
import "./AddProductWizard.css";

// Import marketplace logos
import ebayLogo from "../../assets/marketplaces/ebay.svg";
import shopifyLogo from "../../assets/marketplaces/shopify.svg";
import depopLogo from "../../assets/marketplaces/depop.svg";
import vintedLogo from "../../assets/marketplaces/vinted.svg";
import poshmarkLogo from "../../assets/marketplaces/poshmark.svg";
const blurMessages = [
  "Analyzing image...",
  "Detecting image sharpness...",
  "Checking focus & clarity...",
];
const AddProduct = () => {
  const [currentStep, setCurrentStep] = useState(1);
  // const [isBlurDetectionDisabled, setIsBlurDetectionDisabled] = useState(false);
  const [processingImages, setProcessingImages] = useState(false); // tracks if BG removal / scale-up is running
  const [progress, setProgress] = useState(0); // progress for progress bar

  const [blurTextIndex, setBlurTextIndex] = useState(0);

  const [formData, setFormData] = useState({
    // Step 1 - Images
    uploadedImages: [], // array
    imageProcessing: false,
    backgroundRemoval: false,
    blurDetection: false,
    upscaleImage: false,

    // Step 2 - Listing Details
    title: "",
    description: "",
    sku: "",
    price: "",
    pricerange: "",
    quantity: "1",
    brand: "",
    color: "",
    category: "",
    condition: "",
    material: "",
    tags: "",

    // Variants
    variants: [{ id: 1, size: "", color: "", sku: "" }],

    // Step 3 - Marketplaces
    selectedMarketplaces: [],
  });

  const steps = [
    { id: 1, title: "Imge Upload" },
    { id: 2, title: "Listing Details" },
    { id: 3, title: "Marketplaces" },
    { id: 4, title: "Image Processing" },
    { id: 5, title: "Review" },
  ];
  useEffect(() => {
    if (!formData.imageProcessing) return;

    const interval = setInterval(() => {
      setBlurTextIndex((prev) => (prev + 1) % blurMessages.length);
    }, 500);

    return () => clearInterval(interval);
  }, [formData.imageProcessing]);
  // const categories = [
  //   "Electronics",
  //   "Fashion",
  //   "Home & Garden",
  //   "Sports & Outdoors",
  //   "Toys & Games",
  //   "Beauty & Health",
  //   "Books",
  //   "Automotive",
  //   "Jewelry & Accessories",
  //   "Music & Instruments",
  // ];

  const conditions = [
    { value: "new", label: "New" },
    { value: "like-new", label: "Like New" },
    { value: "excellent", label: "Excellent" },
    { value: "good", label: "Good" },
    { value: "fair", label: "Fair" },
    { value: "poor", label: "Poor" },
  ];

  const marketplaces = [
    { id: "ebay", name: "eBay", logo: ebayLogo },
    { id: "shopify", name: "Shopify", logo: shopifyLogo },
    { id: "poshmark", name: "Poshmark", logo: poshmarkLogo },
    { id: "depop", name: "Depop", logo: depopLogo },
    { id: "vinted", name: "Vinted", logo: vintedLogo },
  ];
  const availableMarketplaces = [
    { id: "ebay", name: "eBay", logo: ebayLogo },
    { id: "shopify", name: "Shopify", logo: shopifyLogo },
    { id: "poshmark", name: "Poshmark", logo: poshmarkLogo },
  ];

  const upcomingMarketplaces = [
    { id: "depop", name: "Depop", logo: depopLogo },
    { id: "vinted", name: "Vinted", logo: vintedLogo },
  ];
  const [processedImages, setProcessedImages] = useState([]);
  const [processing, setProcessing] = useState(false);

  const handleFileUpload = async (event) => {
    const files = Array.from(event.target.files);
    if (!files.length) return;

    const validTypes = ["image/jpeg", "image/png", "image/webp"];
    const maxSize = 5 * 1024 * 1024;

    const validFiles = files.filter((file) => {
      if (!validTypes.includes(file.type)) {
        alert(`${file.name} is not a valid image.`);
        return false;
      }
      if (file.size > maxSize) {
        alert(`${file.name} exceeds 5MB.`);
        return false;
      }
      return true;
    });

    if (!validFiles.length) return;

    setFormData((prev) => ({ ...prev, imageProcessing: true }));

    try {
      // ---------- BLUR CHECK (EXPRESS API) ----------
      const formData = new FormData();
      validFiles.forEach((file) => formData.append("images", file));

      const blurRes = await fetch(
        "https://crosslisting-backend-aacranbzdyd3ffbd.centralindia-01.azurewebsites.net/api/blur/blur-detect",
        {
          method: "POST",
          body: formData,
        }
      );

      if (!blurRes.ok) throw new Error("Blur detection failed");

      const blurResults = await blurRes.json();
      if (!Array.isArray(blurResults)) {
        console.error("Invalid blur response:", blurResults);
        throw new Error("Invalid blur API response");
      }

      console.log("Blur Results:", blurResults);

      // ---------- BUILD ACCEPTED IMAGES AND BLUR STATUSES ----------
      const acceptedImages = [];
      const blurStatuses = [];

      blurResults.forEach((item) => {
        const file = validFiles.find((f) => f.name === item.filename);

        if (!file) {
          console.error("File not found for:", item.filename);
          return;
        }

        const isRejected = item.result.status === "rejected";
        blurStatuses.push(isRejected);

        if (!isRejected) {
          acceptedImages.push(URL.createObjectURL(file));
        } else {
          alert(
            `${item.filename} rejected: ${item.result.reason || "Blurry image"}`
          );
        }
      });

      // ---------- UPDATE STATE ONCE ----------
      setFormData((prev) => ({
        ...prev,
        uploadedImages: [...prev.uploadedImages, ...acceptedImages],
        blurStatuses,
      }));
    } catch (err) {
      console.error("Blur detection error:", err);
      alert("Failed to process images");
    } finally {
      setFormData((prev) => ({ ...prev, imageProcessing: false }));
    }
  };

  // Drag and drop handlers
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      const fakeEvent = { target: { files: files } };
      handleFileUpload(fakeEvent);
    }
  };

  const handleStartImageEditing = async () => {
    if (!formData.uploadedImages.length) return;

    if (!formData.backgroundRemoval && !formData.upscaleImage) {
      alert("Select at least one option");
      return;
    }

    setProcessing(true);
    setProcessedImages([]);

    try {
      const finalImages = [];

      for (let i = 0; i < formData.uploadedImages.length; i++) {
        // base64 → blob → file
        let res = await fetch(formData.uploadedImages[i]);
        let blob = await res.blob();
        let file = new File([blob], `image-${i + 1}.png`, { type: blob.type });

        /* ---------------- 1️⃣ SCALE-UP ---------------- */
        if (formData.upscaleImage) {
          const form = new FormData();
          form.append("images", file);

          const response = await axios.post(
            "https://crosslisting-backend-aacranbzdyd3ffbd.centralindia-01.azurewebsites.net/api/scaleup/scale-up",
            form
          );

          // Scale-up result URL
          const scaledUrl = response.data?.[0]?.result?.url;
          if (!scaledUrl) throw new Error("Scale-up failed");

          // Fetch the scaled image as blob for next step
          const scaledRes = await fetch(scaledUrl);
          const scaledBlob = await scaledRes.blob();
          file = new File([scaledBlob], `scaled-${i + 1}.png`, {
            type: scaledBlob.type,
          });
        }

        /* ---------------- 2️⃣ BACKGROUND REMOVAL ---------------- */
        if (formData.backgroundRemoval) {
          const bgBlob = await removeBackground(file);
          file = new File([bgBlob], `bg-${i + 1}.png`, { type: bgBlob.type });
        }

        // Push final image (either local blob or URL)
        finalImages.push(URL.createObjectURL(file));
      }

      setProcessedImages(finalImages);
    } catch (err) {
      console.error("❌ Image edit error", err);
      alert("Image processing failed");
    } finally {
      setProcessing(false);
    }
  };

  // useEffect(() => {
  //   if (formData.blurStatuses?.length > 0) {
  //     // first image blur hai ya nahi check karo
  //     setIsBlurDetectionDisabled(formData.blurStatuses[0] === true);
  //   } else {
  //     setIsBlurDetectionDisabled(false); // no images yet
  //   }
  // }, [formData.blurStatuses]);

  // Form input handlers
  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleVariantChange = (variantId, field, value) => {
    setFormData((prev) => ({
      ...prev,
      variants: prev.variants.map((variant) =>
        variant.id === variantId ? { ...variant, [field]: value } : variant
      ),
    }));
  };

  const addVariant = () => {
    const newId = Math.max(...formData.variants.map((v) => v.id)) + 1;
    setFormData((prev) => ({
      ...prev,
      variants: [...prev.variants, { id: newId, size: "", color: "", sku: "" }],
    }));
  };

  const removeVariant = (variantId) => {
    if (formData.variants.length > 1) {
      setFormData((prev) => ({
        ...prev,
        variants: prev.variants.filter((variant) => variant.id !== variantId),
      }));
    }
  };

  const handleMarketplaceToggle = (marketplaceId) => {
    setFormData((prev) => ({
      ...prev,
      selectedMarketplaces: prev.selectedMarketplaces.includes(marketplaceId)
        ? prev.selectedMarketplaces.filter((id) => id !== marketplaceId)
        : [...prev.selectedMarketplaces, marketplaceId],
    }));
  };

  // useEffect(() => {
  //   // Step 3 is AFTER Listing (adjust if needed)
  //   if (currentStep === ) {
  //     handleAutoFill();
  //   }
  // }, [currentStep]);

  const nextStep = () => {
    if (currentStep < steps.length) {
      setCurrentStep((prev) => prev + 1);
      if (currentStep === 6) {
        runImageProcessing();
      }
    }
  };

  const runImageProcessing = async () => {
    setProcessingImages(true);
    setProgress(0);

    const finalImages = [];

    for (let i = 0; i < formData.uploadedImages.length; i++) {
      await new Promise((resolve) => setTimeout(resolve, 50)); // yield to UI

      try {
        let res = await fetch(formData.uploadedImages[i]);
        let blob = await res.blob();
        let file = new File([blob], `image-${i + 1}.png`, { type: blob.type });

        if (formData.upscaleImage) {
          const form = new FormData();
          form.append("images", file);
          const response = await axios.post(
            "https://crosslisting-backend-aacranbzdyd3ffbd.centralindia-01.azurewebsites.net/api/scaleup/scale-up",
            form
          );
          const scaledUrl = response.data?.[0]?.result?.url;
          const scaledBlob = await (await fetch(scaledUrl)).blob();
          file = new File([scaledBlob], `scaled-${i + 1}.png`, {
            type: scaledBlob.type,
          });
        }

        if (formData.backgroundRemoval) {
          const bgBlob = await removeBackground(file);
          file = new File([bgBlob], `bg-${i + 1}.png`, { type: bgBlob.type });
        }

        finalImages.push(URL.createObjectURL(file));
        setProcessedImages([...finalImages]); // update UI after each image
        setProgress(
          Math.round(((i + 1) / formData.uploadedImages.length) * 100)
        );
      } catch (err) {
        console.error("Image processing failed for image", i, err);
      }
    }

    setProcessingImages(false);
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep((prev) => prev - 1);
    }
  };

  // Step navigation via stepper click
  const goToStep = (stepId) => {
    // Can only go to previous steps or current step, not future steps
    if (stepId <= getMaxAccessibleStep()) {
      setCurrentStep(stepId);
    }
  };

  const getMaxAccessibleStep = () => {
    let maxStep = 1;

    // Step 2 accessible if at least one image uploaded
    if (formData.uploadedImages.length > 0) maxStep = 2;

    // Step 3 accessible if step 2 required fields filled
    if (
      formData.uploadedImages.length > 0 &&
      formData.title &&
      formData.description &&
      formData.sku &&
      formData.price &&
      formData.quantity
    ) {
      maxStep = 3;
    }

    // Step 4 accessible if step 3 selected marketplaces
    if (
      formData.uploadedImages.length > 0 &&
      formData.title &&
      formData.description &&
      formData.sku &&
      formData.price &&
      formData.quantity &&
      formData.selectedMarketplaces.length > 0
    ) {
      maxStep = 4;
    }

    if (
      formData.uploadedImages.length &&
      formData.title &&
      formData.description &&
      formData.sku &&
      formData.price &&
      formData.quantity &&
      formData.selectedMarketplaces.length > 0
    ) {
      maxStep = 5;
    }

    return maxStep;
  };

  // Check if step is clickable
  const isStepClickable = (stepId) => {
    return stepId <= getMaxAccessibleStep();
  };

  // Validation
  const isStepValid = () => {
    switch (currentStep) {
      case 1:
        return formData.uploadedImages.length > 0;
      case 2:
        return (
          formData.title &&
          formData.description &&
          formData.sku &&
          formData.price &&
          formData.quantity
        );
      case 3:
        return formData.selectedMarketplaces.length > 0;
      case 4:
        return true; // image processing optional
      case 5:
        return true;

      default:
        return false;
    }
  };

  const removeImage = (index) => {
    setFormData((prev) => ({
      ...prev,
      uploadedImages: prev.uploadedImages.filter((_, i) => i !== index),
    }));
  };

  const handleAutoFill = async () => {
    if (!formData.uploadedImages.length) {
      alert("Please upload at least one image first!");
      return;
    }

    try {
      const form = new FormData();

      // convert base64 images to blob
      for (let i = 0; i < formData.uploadedImages.length; i++) {
        const res = await fetch(formData.uploadedImages[i]);
        const blob = await res.blob();
        form.append("images", blob, `product-${i + 1}.png`);
      }

      await sendAutoFillRequest(form);
    } catch (err) {
      console.error("❌ AutoFill Error", err);
      alert("Auto-fill failed");
    }
  };

  const sendAutoFillRequest = async (form) => {
    try {
      const response = await axios.post(
        "https://crosslisting-backend-aacranbzdyd3ffbd.centralindia-01.azurewebsites.net/api/autofill/analyze",
        form,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      const {
        analysis = {},
        product_metadata = {},
        price = {},
      } = response.data;

      // condition mapping
      const conditionScore = Number(analysis.condition || 0);
      let condition = "poor";

      if (conditionScore >= 10) condition = "new";
      else if (conditionScore >= 9) condition = "like-new";
      else if (conditionScore >= 8) condition = "excellent";
      else if (conditionScore >= 7) condition = "good";
      else if (conditionScore >= 6) condition = "fair";

      setFormData((prev) => ({
        ...prev,

        // SEO first preference
        title: analysis.seo_title || product_metadata.title || prev.title,

        description:
          analysis.seo_description ||
          product_metadata.description ||
          prev.description,

        sku: product_metadata.sku || prev.sku,
        brand: analysis.brand || "Others",
        category: product_metadata.category || prev.category,
        subcategory: product_metadata.subcategory || prev.subcategory,
        gender: analysis.gender || prev.gender,
        material: analysis.material || prev.material,
        color: analysis.color || prev.color,

        price: price.recommended || prev.price,
        pricerange:
          price.low && price.high
            ? `${price.low} - ${price.high}`
            : prev.pricerange,

        condition,

        tags: analysis.seo_tags
          ? analysis.seo_tags.split(",").map((t) => t.trim())
          : product_metadata.seo_tags
          ? product_metadata.seo_tags.split(",").map((t) => t.trim())
          : prev.tags,
      }));

      console.log("✅ Auto-fill success", response.data);
    } catch (err) {
      console.error("❌ API Error", err);
      alert("Auto-fill failed");
    }
  };

  const handleSubmit = () => {
    alert("Product listing created successfully! (Demo)");
  };

  const handleExportCSV = () => {
    if (!formData) return;

    // CSV headers
    const headers = [
      "Marketplace",
      "Title",
      "Description",
      "SKU",
      "Price",
      "Quantity",
      "Brand",
      "Category",
      "Condition",
      "Color",
      "Variants",
    ];

    // Prepare rows
    const rows = [];

    // Main listing (general)
    rows.push([
      "All Marketplaces",
      formData.title,
      formData.description,
      formData.sku,
      formData.price,
      formData.quantity,
      formData.brand,
      formData.category,
      formData.condition,
      formData.color,
      formData.variants
        .map((v) => `Size: ${v.size}, Color: ${v.color}, SKU: ${v.sku}`)
        .join(" | "),
    ]);

    // Marketplace-specific
    formData.selectedMarketplaces.forEach((id) => {
      let marketplaceName = marketplaces.find((m) => m.id === id)?.name || id;

      let row = [
        marketplaceName,
        formData.title,
        formData.description,
        formData.sku,
        formData.price,
        formData.quantity,
        formData.brand,
        formData.category,
        formData.condition,
        formData.color,
        formData.variants
          .map((v) => `Size: ${v.size}, Color: ${v.color}, SKU: ${v.sku}`)
          .join(" | "),
      ];

      rows.push(row);
    });

    // Convert to CSV string
    const csvContent = [
      headers.join(","),
      ...rows.map((r) => r.map((v) => `"${v}"`).join(",")),
    ].join("\n");

    // Create blob and download
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "product_listing.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <MDBContainer fluid className="add-product-wizard">
      <div className="wizard-container">
        {/* Header */}
        <div className="wizard-header">
          <h1 className="wizard-title">Add Product</h1>
          <p className="wizard-subtitle">
            Create a new product listing in 5 simple steps
          </p>
        </div>

        {/* Stepper */}
        <div className="stepper">
          {steps.map((step, index) => (
            <div key={step.id} className="step-container">
              <div
                className={`step ${currentStep >= step.id ? "active" : ""} ${
                  currentStep === step.id ? "current" : ""
                } ${isStepClickable(step.id) ? "clickable" : "disabled"}`}
              >
                <div
                  className="step-number"
                  onClick={() => goToStep(step.id)}
                  style={{
                    cursor: isStepClickable(step.id) ? "pointer" : "default",
                  }}
                >
                  {currentStep > step.id ? "✓" : step.id}
                </div>
                <div className="step-label">{step.title}</div>
              </div>
              {index < steps.length - 1 && (
                <div
                  className={`step-connector ${
                    currentStep > step.id ? "completed" : ""
                  }`}
                ></div>
              )}
            </div>
          ))}
        </div>

        {/* Step Content */}
        <div className="step-content">
          {/* STEP 1: IMAGES */}
          {currentStep === 1 && (
            <div className="step-panel">
              <div className="step-header">
                <h2>Upload Product Images</h2>
                <p>Add high-quality images of your product</p>
              </div>

              <div
                className="upload-area"
                onDragOver={handleDragOver}
                onDrop={handleDragDrop}
                onClick={() =>
                  !formData.imageProcessing &&
                  document.getElementById("file-input").click()
                }
              >
                <input
                  id="file-input"
                  type="file"
                  accept="image/*"
                  capture="environment"
                  multiple
                  style={{ display: "none" }}
                  onChange={handleFileUpload}
                />

                {formData.imageProcessing ? (
                  <div className="skeleton-loader">
                    <div className="skeleton-shimmer"></div>

                    <span className="blur-detect-text">
                      {blurMessages[blurTextIndex]}
                    </span>
                  </div>
                ) : formData.uploadedImages.length > 0 ? (
                  <div className="image-preview-container">
                    {formData.uploadedImages.map((img, index) => (
                      <div key={index} className="image-preview">
                        <img src={img} alt={`Product ${index + 1}`} />

                        {/* ❌ Remove */}
                        <span
                          className="image-cross"
                          onClick={(e) => {
                            e.stopPropagation();
                            removeImage(index);
                          }}
                        >
                          ×
                        </span>

                        {/* ✅ Blur Badge */}
                        <span
                          className={`blur-badge ${
                            formData.blurStatuses[index] ? "blurred" : "ok"
                          }`}
                        >
                          {formData.blurStatuses[index] ? "Blur!" : "OK"}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="upload-placeholder">
                    <div className="upload-icon">📷</div>
                    <h3>Drop Images OR Click to Upload</h3>
                    <p>JPG, PNG, WebP • Max 5MB</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* STEP 2: LISTING DETAILS */}
          {currentStep === 2 && (
            <div className="step-panel">
              {processingImages && (
                <div className="progress-bar-wrapper my-3">
                  <div
                    className="progress-bar-bg"
                    style={{
                      width: "100%",
                      height: "6px",
                      background: "#eee",
                      borderRadius: "4px",
                    }}
                  >
                    <div
                      className="progress-bar-fill"
                      style={{
                        width: `${progress}%`,
                        height: "100%",
                        background: "#6a1b9a",
                        borderRadius: "4px",
                        transition: "width 0.3s",
                      }}
                    ></div>
                  </div>
                  <span style={{ fontSize: "14px", color: "#555" }}>
                    {progress}% {blurMessages[blurTextIndex]}
                  </span>
                </div>
              )}

              <div className="step-header">
                <h2>Listing Details</h2>
                <p>Add comprehensive product information</p>
                <MDBBtn
                  color="light"
                  size="sm"
                  className="auto-fill-btn"
                  onClick={handleAutoFill}
                >
                  Auto-Fill Listing
                </MDBBtn>
              </div>

              <MDBRow>
                {/* Required Fields Section */}
                <MDBCol md="12" className="mb-3">
                  <h4 className="section-title">Required Information</h4>
                </MDBCol>

                <MDBCol md="12">
                  <MDBInput
                    label="Product Title *"
                    type="text"
                    size="lg"
                    className="mb-3"
                    value={formData.title}
                    onChange={(e) => handleInputChange("title", e.target.value)}
                    required
                  />
                </MDBCol>

                <MDBCol md="12">
                  <MDBTextArea
                    label="Product Description *"
                    rows="4"
                    className="mb-3"
                    value={formData.description}
                    onChange={(e) =>
                      handleInputChange("description", e.target.value)
                    }
                    required
                  />
                </MDBCol>

                <MDBCol md="4">
                  <MDBInput
                    label="SKU *"
                    type="text"
                    size="lg"
                    className="mb-3"
                    value={formData.sku}
                    onChange={(e) => handleInputChange("sku", e.target.value)}
                    required
                  />
                </MDBCol>

                <MDBCol md="4">
                  <MDBInput
                    label="Price *"
                    type="number"
                    size="lg"
                    className="mb-3"
                    value={formData.price}
                    onChange={(e) => handleInputChange("price", e.target.value)}
                    required
                    step="0.01"
                    min="0"
                  />
                </MDBCol>
                <MDBCol md="4">
                  <MDBInput
                    label="Price Range"
                    type="text"
                    size="lg"
                    className="mb-3"
                    value={formData.pricerange}
                    onChange={(e) =>
                      handleInputChange("pricerange", e.target.value)
                    }
                  />
                </MDBCol>

                <MDBCol md="4">
                  <MDBInput
                    label="Quantity *"
                    type="number"
                    size="lg"
                    className="mb-3"
                    value={formData.quantity}
                    onChange={(e) =>
                      handleInputChange("quantity", e.target.value)
                    }
                    required
                    min="1"
                  />
                </MDBCol>

                {/* Additional Details Section */}
                <MDBCol md="12" className="mb-3 mt-4">
                  <h4 className="section-title">Additional Details</h4>
                </MDBCol>

                <MDBCol md="6">
                  <MDBInput
                    label="Brand"
                    type="text"
                    size="lg"
                    className="mb-3"
                    value={formData.brand}
                    onChange={(e) => handleInputChange("brand", e.target.value)}
                  />
                </MDBCol>
                <MDBCol md="6">
                  <MDBInput
                    label="Color"
                    type="text"
                    size="lg"
                    className="mb-3"
                    value={formData.color}
                    onChange={(e) => handleInputChange("color", e.target.value)}
                  />
                </MDBCol>

                <MDBCol md="6">
                  <MDBInput
                    label="Category"
                    type="text"
                    size="lg"
                    className="mb-3"
                    value={formData.category}
                    onChange={(e) =>
                      handleInputChange("category", e.target.value)
                    }
                  />
                </MDBCol>

                <MDBCol md="4">
                  <select
                    className="form-select form-select-lg mb-3"
                    value={formData.condition}
                    onChange={(e) =>
                      handleInputChange("condition", e.target.value)
                    }
                  >
                    <option value="">Select Condition</option>
                    {conditions.map((condition) => (
                      <option key={condition.value} value={condition.value}>
                        {condition.label}
                      </option>
                    ))}
                  </select>
                </MDBCol>

                <MDBCol md="4">
                  <MDBInput
                    label="Material"
                    type="text"
                    size="lg"
                    className="mb-3"
                    value={formData.material}
                    onChange={(e) =>
                      handleInputChange("material", e.target.value)
                    }
                    step="0.1"
                    min="0"
                  />
                </MDBCol>

                <MDBCol md="12">
                  <MDBInput
                    label="Tags / Keywords"
                    type="text"
                    size="lg"
                    className="mb-3"
                    value={formData.tags}
                    onChange={(e) => handleInputChange("tags", e.target.value)}
                    placeholder="Comma separated tags"
                  />
                </MDBCol>
              </MDBRow>

              {/* Product Variants */}
              <div className="variants-section">
                <div className="variants-header">
                  <h4 className="section-title">Product Variants</h4>
                  <MDBBtn
                    size="sm"
                    className="btn-red-outline"
                    onClick={addVariant}
                  >
                    + Add Variant
                  </MDBBtn>
                </div>

                {formData.variants.map((variant, index) => (
                  <div key={variant.id} className="variant-card">
                    <div className="variant-header">
                      <h5>Variant {index + 1}</h5>
                      {formData.variants.length > 1 && (
                        <MDBBtn
                          size="sm"
                          color="light"
                          onClick={() => removeVariant(variant.id)}
                        >
                          Remove
                        </MDBBtn>
                      )}
                    </div>

                    <MDBRow>
                      <MDBCol md="4">
                        <MDBInput
                          label="Size"
                          type="text"
                          className="mb-3"
                          value={variant.size}
                          onChange={(e) =>
                            handleVariantChange(
                              variant.id,
                              "size",
                              e.target.value
                            )
                          }
                        />
                      </MDBCol>
                      <MDBCol md="4">
                        <MDBInput
                          label="Color"
                          type="text"
                          className="mb-3"
                          value={variant.color}
                          onChange={(e) =>
                            handleVariantChange(
                              variant.id,
                              "color",
                              e.target.value
                            )
                          }
                        />
                      </MDBCol>
                      <MDBCol md="4">
                        <MDBInput
                          label="Variant SKU"
                          type="text"
                          className="mb-3"
                          value={variant.sku}
                          onChange={(e) =>
                            handleVariantChange(
                              variant.id,
                              "sku",
                              e.target.value
                            )
                          }
                        />
                      </MDBCol>
                    </MDBRow>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* STEP 3: MARKETPLACES */}
          {currentStep === 3 && (
            <div className="step-panel">
              {processingImages && (
                <div className="progress-bar-wrapper my-3">
                  <div
                    className="progress-bar-bg"
                    style={{
                      width: "100%",
                      height: "6px",
                      background: "#eee",
                      borderRadius: "4px",
                    }}
                  >
                    <div
                      className="progress-bar-fill"
                      style={{
                        width: `${progress}%`,
                        height: "100%",
                        background: "#6a1b9a",
                        borderRadius: "4px",
                        transition: "width 0.3s",
                      }}
                    ></div>
                  </div>
                  <span style={{ fontSize: "14px", color: "#555" }}>
                    {progress}% Processing Images...
                  </span>
                </div>
              )}

              <div className="step-header">
                <h2>Select Marketplaces</h2>
                <p>Choose where you want to list your product</p>
              </div>

              <div className="marketplace-grid">
                {availableMarketplaces.map((marketplace) => (
                  <div
                    key={marketplace.id}
                    className={`marketplace-card ${
                      formData.selectedMarketplaces.includes(marketplace.id)
                        ? "selected"
                        : ""
                    }`}
                  >
                    <div
                      className="marketplace-select-area"
                      onClick={() => handleMarketplaceToggle(marketplace.id)}
                    >
                      <div className="marketplace-logo">
                        <img src={marketplace.logo} alt={marketplace.name} />
                      </div>
                      <h4>{marketplace.name}</h4>
                      <div className="marketplace-check">
                        {formData.selectedMarketplaces.includes(marketplace.id)
                          ? "✓"
                          : ""}
                      </div>
                    </div>

                    <MDBBtn
                      size="sm"
                      color="light"
                      className="integrate-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        alert(`${marketplace.name} integration coming next`);
                      }}
                    >
                      Integrate
                    </MDBBtn>
                  </div>
                ))}

                {/* Upcoming marketplaces */}
                {upcomingMarketplaces.map((marketplace) => (
                  <div
                    key={marketplace.id}
                    className="marketplace-card upcoming"
                  >
                    <div className="marketplace-logo">
                      <img
                        src={marketplace.logo}
                        alt={`${marketplace.name} logo`}
                      />
                    </div>
                    <h4>{marketplace.name}</h4>
                    <div className="marketplace-badge">Coming Soon</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* STEP 4: REVIEW */}
          {/* STEP 4: IMAGE PROCESSING */}
          {currentStep === 4 && (
            <div className="step-panel">
              <div className="step-header">
                <h2>Image Processing</h2>
                <p>Select enhancements to apply to your images</p>
              </div>

              <div className="image-options">
                <div className="checkbox-wrapper">
                  <MDBCheckbox
                    label="Background Removal"
                    checked={formData.backgroundRemoval}
                    onChange={(e) =>
                      handleInputChange("backgroundRemoval", e.target.checked)
                    }
                  />
                </div>

                <div className="checkbox-wrapper">
                  <MDBCheckbox
                    label="Upscale Image"
                    checked={formData.upscaleImage}
                    onChange={(e) =>
                      handleInputChange("upscaleImage", e.target.checked)
                    }
                  />
                </div>
              </div>

              <MDBBtn
                className="mt-3"
                disabled={processing}
                onClick={handleStartImageEditing}
              >
                {processing ? "Processing..." : "Start Image Processing"}
              </MDBBtn>

              {processingImages && (
                <div className="progress-bar-wrapper mt-3">
                  <div className="progress-bar-bg">
                    <div
                      className="progress-bar-fill"
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                  <span>{progress}% Processing</span>
                </div>
              )}

              {processedImages.length > 0 && (
                <div className="processed-images mt-4">
                  {processedImages.map((img, i) => (
                    <img key={i} src={img} alt={`Processed ${i}`} />
                  ))}
                </div>
              )}
            </div>
          )}

          {currentStep === 5 && (
            <div className="step-panel">
              <div className="step-header">
                <h2>Review & Submit</h2>
                <p>Please review your product details before submitting</p>
              </div>

              <div className="review-sections">
                {/* Image Review */}

                <div className="review-section">
                  <h4>Product Images</h4>
                  <div className="review-image">
                    {(processedImages.length
                      ? processedImages
                      : formData.uploadedImages
                    ).map((img, index) => (
                      <img key={index} src={img} alt={`Product ${index + 1}`} />
                    ))}
                  </div>
                </div>

                {/* Product Details Review */}
                <div className="review-section">
                  <h4>Product Details</h4>
                  <div className="review-grid">
                    <div className="review-item">
                      <strong>Title:</strong> {formData.title}
                    </div>
                    <div className="review-item">
                      <strong>Description:</strong> {formData.description}
                    </div>
                    <div className="review-item">
                      <strong>SKU:</strong> {formData.sku}
                    </div>
                    <div className="review-item">
                      <strong>Price:</strong> ${formData.price}
                    </div>
                    <div className="review-item">
                      <strong>Quantity:</strong> {formData.quantity}
                    </div>
                    {formData.brand && (
                      <div className="review-item">
                        <strong>Brand:</strong> {formData.brand}
                      </div>
                    )}
                    {formData.category && (
                      <div className="review-item">
                        <strong>Category:</strong> {formData.category}
                      </div>
                    )}
                  </div>
                </div>

                {/* Variants Review */}
                {formData.variants.some((v) => v.size || v.color || v.sku) && (
                  <div className="review-section">
                    <h4>Product Variants</h4>
                    <div className="variants-review">
                      {formData.variants.map((variant, index) => (
                        <div key={variant.id} className="variant-review-item">
                          <strong>Variant {index + 1}:</strong>
                          {variant.size && <span>Size: {variant.size}</span>}
                          {variant.color && <span>Color: {variant.color}</span>}
                          {variant.sku && <span>SKU: {variant.sku}</span>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Marketplaces Review */}
                <div className="review-section">
                  <h4>Selected Marketplaces</h4>
                  <div className="selected-marketplaces">
                    {formData.selectedMarketplaces.map((id) => {
                      const marketplace = marketplaces.find((m) => m.id === id);
                      if (!marketplace) return null;

                      return (
                        <div key={id} className="marketplace-preview">
                          <span className="marketplace-badge">
                            {marketplace.name}
                          </span>

                          {/* Marketplace-specific product details */}
                          <div className="marketplace-details">
                            {id === "shopify" && (
                              <div className="review-grid">
                                <div className="review-item">
                                  <strong>Title:</strong> {formData.title}
                                </div>
                                <div className="review-item">
                                  <strong>Description:</strong>{" "}
                                  {formData.description}
                                </div>
                                <div className="review-item">
                                  <strong>Price:</strong> ${formData.price}
                                </div>
                                <div className="review-item">
                                  <strong>SKU:</strong> {formData.sku}
                                </div>
                              </div>
                            )}

                            {id === "ebay" && (
                              <div className="review-grid">
                                <div className="review-item">
                                  <strong>Title:</strong> {formData.title}
                                </div>
                                <div className="review-item">
                                  <strong>SKU:</strong> {formData.sku}
                                </div>
                                <div className="review-item">
                                  <strong>Price:</strong> ${formData.price}
                                </div>
                              </div>
                            )}

                            {id === "poshmark" && (
                              <div className="review-grid">
                                <div className="review-item">
                                  <strong>Title:</strong> {formData.title}
                                </div>
                                <div className="review-item">
                                  <strong>Price:</strong> ${formData.price}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Navigation */}
        <div className="wizard-navigation">
          <MDBBtn
            color="light"
            disabled={currentStep === 1}
            onClick={prevStep}
            className="nav-btn"
          >
            Back
          </MDBBtn>

          {currentStep < steps.length ? (
            <MDBBtn
              className="btn-red"
              disabled={!isStepValid()}
              onClick={nextStep}
            >
              Next
            </MDBBtn>
          ) : (
            <>
              <MDBBtn
                className="btn-red"
                onClick={handleSubmit}
                disabled={processingImages}
              >
                Create Listing
              </MDBBtn>

              {/* Export CSV Button */}
              <MDBBtn
                className="btn-outline-dark ms-3"
                onClick={handleExportCSV}
                disabled={processingImages}
              >
                Export CSV
              </MDBBtn>
            </>
          )}
        </div>
      </div>
    </MDBContainer>
  );
};

export default AddProduct;
